import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import IncomeStatementCurrent from '../components/financial/IncomeStatementCurrent';
import { Table, History, Image } from 'lucide-react';
import IncomeStatementHistory from '../components/financial/IncomeStatementHistory';
import BalanceSheetHistory from '../components/financial/BalanceSheetHistory';
import CashFlowHistory from '../components/financial/CashFlowHistory';
import ChartOverview from '../components/financial/ChartOverview';
import BalanceSheetCurrent from '../components/financial/BalanceSheetCurrent';
import CashFlowCurrentImproved from '../components/financial/CashFlowCurrentImproved';
import ProductSegments from '../components/financial/ProductSegments';
import GeographicSegments from '../components/financial/GeographicSegments';
import StockService from '../Api_Module/Api_Services/StockService';

export function Financial() {
  const [showSecHtml, setShowSecHtml] = useState(false);
  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement | null>(null);
  const [htmlContent, setHtmlContent] = useState<string>("");
  const {
    subsection,
    symbol
  } = useParams<{
    symbol?: string;
    subsection?: string;
  }>();
  // console.log('Financial subsection:', subsection);
  // console.log('Financial symbol:', symbol);

  useEffect(() => {
    if (!subsection) {
      setActiveSection('income-statement');
    } else {
      setActiveSection(subsection);
    }
  }, [subsection]);

  useEffect(() => {
    // ============================================
    // FEATURE TEMPORARILY DISABLED - SEC Files API
    // Date disabled: January 23, 2026
    // Causing 500 Internal Server Error
    // To re-enable: Uncomment the fetchHtmlContent call
    // ============================================

    // const fetchHtmlContent = async () => {
    //   try {
    //     const response = await StockService.getSecFiles(symbol);
    //     if (response) {
    //       const htmlText = await response?.htmlContent;
    //       setHtmlContent(htmlText);
    //     }
    //   } catch (error) {
    //     console.error('Error fetching HTML file:', error);
    //     setHtmlContent('<div>Error loading SEC document</div>');
    //   }
    // };

    // fetchHtmlContent();
  }, []);

  const [activeSection, setActiveSection] = useState<string>(subsection || 'income-statement');

  // ============================================
  // FEATURE CHANGE: Default to 'history' instead of 'current'
  // Date changed: January 23, 2026
  // Income Statement and Cash Flow now default to history view
  // Balance Sheet still has both tabs
  // ============================================
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('history');
  const renderContent = () => {
    switch (activeSection) {
      case 'income-statement':
        // Always show History view for Income Statement (Current tab removed)
        return <IncomeStatementHistory symbol={symbol} />;
      case 'balance-sheet':
        // Balance Sheet keeps both Current and History tabs
        return (
          activeTab === 'current' ?
            <BalanceSheetCurrent symbol={symbol} /> : <BalanceSheetHistory symbol={symbol} />
        );
      case 'cash-flow':
        // Always show History view for Cash Flow (Current tab removed)
        return <CashFlowHistory symbol={symbol} />;
      case 'chart-analysis':
        return (
          <ChartOverview symbol={symbol} />
        );
      case 'product-segments':
        return (
          <>
            {/* Product Segments Container */}
            <div className="bg-theme-secondary rounded-lg shadow-lg border border-theme-tertiary">
              <ProductSegments symbol={symbol} />
            </div>

            {/* Geographic Segments Container */}
            <div className="bg-theme-secondary rounded-lg shadow-lg border border-theme-tertiary">
              <GeographicSegments symbol={symbol} />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const searchKeys = [
    { label: 'Revenue', searchTerms: ['revenue', 'net sales', 'total revenue'] },
    { label: 'Net Income', searchTerms: ['net income', 'net earnings', 'profit'] },
    // { label: 'Operating Income', searchTerms: ['operating income', 'operating profit'] },
    // { label: 'Cash Flow', searchTerms: ['cash flow', 'operating cash flow'] },
    // { label: 'Assets', searchTerms: ['total assets', 'current assets'] },
    // { label: 'Liabilities', searchTerms: ['total liabilities', 'current liabilities'] },
    { label: 'FORM 4', searchTerms: ['form 4'] },
    { label: "Palantir Technologies", searchTerms: ['palantir technologies', 'pltr'] }
  ];

  const searchInIframe = (searchTerms: string[]) => {
    if (!iframeRef) return;

    try {
      const iframeDoc = iframeRef.contentDocument || iframeRef.contentWindow?.document;
      if (!iframeDoc) return;
      const existingHighlights = iframeDoc.querySelectorAll('.search-highlight');
      existingHighlights.forEach(el => {
        const parent = el.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(el.textContent || ''), el);
          parent.normalize();
        }
      });

      for (const term of searchTerms) {
        const walker = iframeDoc.createTreeWalker(
          iframeDoc.body,
          NodeFilter.SHOW_TEXT,
          null
        );

        const textNodes: Text[] = [];
        let node;
        while (node = walker.nextNode()) {
          textNodes.push(node as Text);
        }

        for (const textNode of textNodes) {
          const text = textNode.textContent || '';
          const regex = new RegExp(term, 'gi');
          if (regex.test(text)) {
            const highlightedText = text.replace(regex, `<span class="search-highlight" style="background-color: yellow; padding: 2px;">${term}</span>`);
            const wrapper = iframeDoc.createElement('div');
            wrapper.innerHTML = highlightedText;
            textNode.parentNode?.replaceChild(wrapper, textNode);
            const firstHighlight = iframeDoc.querySelector('.search-highlight');
            if (firstHighlight) {
              firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error searching in iframe:', error);
    }
  };

  const containerClass = 'w-full';

  return <div className={containerClass}>
    {showSecHtml && (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div className="bg-theme-secondary rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex">
          <div className="w-64 bg-theme-tertiary border-r border-theme-tertiary p-4">
            <h4 className="text-sm font-medium text-theme-primary mb-4">Quick Search</h4>
            <div className="space-y-2">
              {searchKeys.map((key, index) => (
                <button
                  key={index}
                  onClick={() => searchInIframe(key.searchTerms)}
                  className="w-full text-left px-3 py-2 text-xs bg-theme-secondary hover:bg-blue-600/20 text-theme-secondary hover:text-blue-400 rounded-sm transition-colors border border-theme-tertiary"
                >
                  {key.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 flex flex-col">
            <div className="p-3 border-b border-theme-tertiary flex justify-between items-center">
              <h3 className="text-sm font-medium text-theme-primary">
                SEC Financial Document
              </h3>
              <button
                onClick={() => setShowSecHtml(false)}
                className="p-1 hover:bg-theme-tertiary rounded-full"
              >
                <svg className="w-5 h-5 text-theme-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-hidden">
              <iframe
                ref={setIframeRef}
                srcDoc={htmlContent || '<div>Loading...</div>'}
                className="w-full h-full border-none"
                title="SEC Financial Document"

                style={{ overflowY: 'auto', height: 'calc(90vh - 80px)', backgroundColor: 'white' }}
                onLoad={() => {
                  if (iframeRef) {
                    try {
                      const iframeDoc = iframeRef.contentDocument;
                      if (iframeDoc) {
                        // const style = iframeDoc.createElement('style');
                        // style.textContent = `
                        //   body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
                        //   .search-highlight { background-color: yellow !important; padding: 2px !important; }
                        // `;
                        // iframeDoc.head.appendChild(style);
                      }
                    } catch (error) {
                      console.error('Error styling iframe:', error);
                    }
                  }
                }}
              />
            </div>

            {/* <div className="p-3 border-t border-theme-tertiary bg-theme-tertiary/30 text-xs text-theme-muted">
              Source: SEC EDGAR Database - Click search keys on the left to navigate to specific sections
            </div> */}
          </div>
        </div>
      </div>
    )}

    <div className="transition-all duration-300">
      <div className="space-y-4">
        {/* ============================================
            FEATURE CHANGE: Tabs only shown for Balance Sheet
            Date changed: January 23, 2026
            Income Statement and Cash Flow no longer have Current/History tabs
            Balance Sheet still has both tabs
            ============================================ */}
        {activeSection === 'balance-sheet' && (
          <div className="bg-theme-secondary shadow-lg py-2 px-4 mb-0 border-theme-tertiary">
            <div className="flex space-x-1 p-0.5 w-fit">
              <button onClick={() => setActiveTab('current')} className={`
                    flex items-center px-3 py-1.5 text-xs font-medium rounded-sm transition-colors btn
                    ${activeTab === 'current' ? 'active' : ''}
                  `}>
                <Table size={12} className="mr-1.5" />
                Current
              </button>
              <button onClick={() => setActiveTab('history')} className={`
                    flex items-center px-3 py-1.5 text-xs font-medium rounded-sm transition-colors btn
                    ${activeTab === 'history' ? 'active' : ''}
                  `}>
                <History size={12} className="mr-1.5" />
                History
              </button>
            </div>
          </div>)}
        {/* {activeSection === 'chart-analysis' || activeTab === 'current' &&
          <div className='w-full flex bg-theme-secondary justify-end shadow-lg px-5 pt-3 border-theme-tertiary'>
            <div className="flex space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSecHtml(true);
                }}
                className="flex items-center text-xs bg-blue-600/20 hover:bg-blue-600/30 text-blue-500 px-2 py-1 rounded-sm transition-colors"
                title="View SEC Document"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                SEC Document
              </button>
            </div>
          </div>} */}
        {renderContent()}
        
      </div>
    </div>
  </div>;
}